'use client';

import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import { Loader2, Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
interface PaintingPrompt {
  id: string;
  imageUrl: string;
  prompt: string;
  width: number;
  height: number;
  aspectRatio?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}
interface PromptManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function PromptManagementModal({
  open,
  onOpenChange
}: PromptManagementModalProps) {
  const [prompts, setPrompts] = useState<PaintingPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<PaintingPrompt | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    imageUrl: '',
    prompt: '',
    width: 600,
    height: 400
  });
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const data = new FormData();
      data.append('files', file);
      const response = await fetch('/api/storage/upload-image', {
        method: 'POST',
        body: data
      });
      const result = await response.json();
      if (!response.ok || result.code !== 0) {
        throw new Error(result.msg || 'Upload failed');
      }
      if (result.data?.urls?.[0]) {
        const url = result.data.urls[0];
        setFormData(prev => ({
          ...prev,
          imageUrl: url
        }));
        toast.success('Image uploaded successfully');

        // Auto-detect dimensions
        const img = new Image();
        img.src = url;
        img.onload = () => {
          setFormData(prev_0 => ({
            ...prev_0,
            width: img.naturalWidth,
            height: img.naturalHeight
          }));
        };
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // Fetch all prompts (including inactive)
  const fetchPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const response_0 = await fetch('/api/admin/painting-prompts?limit=1000');
      const result_0 = await response_0.json();
      if (result_0.data) {
        setPrompts(result_0.data.prompts || []);
      } else {
        console.error('Invalid response structure:', result_0);
      }
    } catch (error_0) {
      console.error('Failed to fetch prompts:', error_0);
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) {
      fetchPrompts();
    }
  }, [open, fetchPrompts]);
  const handleEdit = (prompt: PaintingPrompt) => {
    setSelectedPrompt(prompt);
    setFormData({
      imageUrl: prompt.imageUrl,
      prompt: prompt.prompt,
      width: prompt.width,
      height: prompt.height
    });
    setIsEditing(true);
  };
  const handleCreate = () => {
    setSelectedPrompt(null);
    setFormData({
      imageUrl: '',
      prompt: '',
      width: 600,
      height: 400
    });
    setIsEditing(true);
  };
  const handleSave = async () => {
    // Validation
    if (!formData.imageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    if (!formData.prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    if (formData.width <= 0 || formData.height <= 0) {
      toast.error('Width and height must be greater than 0');
      return;
    }
    try {
      setLoading(true);
      if (selectedPrompt) {
        // Update existing prompt
        const response_1 = await fetch(`/api/admin/painting-prompts/${selectedPrompt.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageUrl: formData.imageUrl.trim(),
            prompt: formData.prompt.trim(),
            width: formData.width,
            height: formData.height
          })
        });
        const result_1 = await response_1.json();
        if (!response_1.ok) {
          throw new Error(result_1.message || 'Failed to update painting prompt');
        }
        toast.success('Prompt updated successfully!');
      } else {
        // Create new prompt
        const response_2 = await fetch('/api/admin/painting-prompts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageUrl: formData.imageUrl.trim(),
            prompt: formData.prompt.trim(),
            width: formData.width,
            height: formData.height
          })
        });
        const result_2 = await response_2.json();
        if (!response_2.ok) {
          throw new Error(result_2.message || 'Failed to create painting prompt');
        }
        toast.success('Prompt created successfully!');
      }
      setIsEditing(false);
      setSelectedPrompt(null);
      await fetchPrompts();
    } catch (error_1: any) {
      toast.error(error_1.message || 'Failed to save prompt');
      console.error('Save error:', error_1);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) {
      return;
    }
    try {
      setLoading(true);
      const response_3 = await fetch(`/api/admin/painting-prompts/${id}`, {
        method: 'DELETE'
      });
      const result_3 = await response_3.json();
      if (!response_3.ok) {
        throw new Error(result_3.message || 'Failed to delete painting prompt');
      }
      toast.success('Prompt deleted successfully!');
      await fetchPrompts();
    } catch (error_2: any) {
      toast.error(error_2.message || 'Failed to delete prompt');
      console.error('Delete error:', error_2);
    } finally {
      setLoading(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-full max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Manage Painting Prompts</DialogTitle>
        </DialogHeader>

        {isEditing ?
      // Edit/Create Form
      <div className="space-y-4 py-4">
            {/* Image Upload/URL */}
            <div className="space-y-3">
              <Label>Image Source</Label>
              
              <div className="flex flex-col gap-4 border p-4 rounded-md bg-muted/30">
                 {/* File Input */}
                 <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground font-normal">Option 1: Upload Local Image</Label>
                    <div className="flex items-center gap-2">
                      <Input type="file" accept="image/*" onChange={handleFileSelect} disabled={loading || uploading} className="cursor-pointer bg-background" />
                      {uploading && <Loader2 className="animate-spin h-4 w-4 text-primary" />}
                    </div>
                 </div>
                 
                 <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-muted/30 px-2 text-muted-foreground">Or</span>
                    </div>
                 </div>

                 {/* URL Input */}
                 <div className="space-y-2">
                    <Label htmlFor="imageUrl" className="text-xs text-muted-foreground font-normal">Option 2: Image URL</Label>
                    <Input id="imageUrl" type="url" placeholder="https://example.com/image.jpg" value={formData.imageUrl} onChange={e_0 => setFormData({
                ...formData,
                imageUrl: e_0.target.value
              })} disabled={loading || uploading} className="bg-background" />
                 </div>
              </div>
            </div>

            {formData.imageUrl && <div className="relative rounded-md overflow-hidden border bg-muted aspect-video flex items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={formData.imageUrl} alt="Preview" className="max-h-full max-w-full object-contain" onLoad={e_1 => {
            const img_0 = e_1.currentTarget;
            setFormData(prev_1 => ({
              ...prev_1,
              width: img_0.naturalWidth,
              height: img_0.naturalHeight
            }));
          }} />
              </div>}

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt</Label>
              <Textarea id="prompt" placeholder="Describe the painting prompt in detail..." value={formData.prompt} onChange={e_2 => setFormData({
            ...formData,
            prompt: e_2.target.value
          })} disabled={loading} rows={4} />
              <p className="text-xs text-muted-foreground">
                {formData.prompt.length} / 10000 characters
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="width">Width (px)</Label>
                <Input id="width" type="number" min="1" value={formData.width} onChange={e_3 => setFormData({
              ...formData,
              width: Math.max(1, parseInt(e_3.target.value) || 1)
            })} disabled={loading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="height">Height (px)</Label>
                <Input id="height" type="number" min="1" value={formData.height} onChange={e_4 => setFormData({
              ...formData,
              height: Math.max(1, parseInt(e_4.target.value) || 1)
            })} disabled={loading} />
              </div>
            </div>

            <div className="bg-muted p-3 rounded-md text-sm">
              <p>
                Aspect Ratio: {(formData.width / formData.height).toFixed(2)}:1
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)} disabled={loading} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1" onClick={handleSave}>
                {loading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </> : <>
                    <Plus className="mr-2 h-4 w-4" />
                    {selectedPrompt ? 'Update' : 'Create'}
                  </>}
              </Button>
            </div>
          </div> :
      // List View
      <div className="space-y-4 py-4">
            <Button onClick={handleCreate} disabled={loading} className="w-full">
              <Plus className="mr-2 h-4 w-4" />
              Create New Prompt
            </Button>

            {loading && prompts.length === 0 ? <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin" size={40} />
              </div> : prompts.length === 0 ? <div className="text-center py-8">
                <p className="text-muted-foreground">No prompts available.</p>
              </div> : <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Image</TableHead>
                      <TableHead className="max-w-xs">Prompt</TableHead>
                      <TableHead className="w-20">Size</TableHead>
                      <TableHead className="w-20">Status</TableHead>
                      <TableHead className="w-32">Created</TableHead>
                      <TableHead className="w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prompts.map(prompt_0 => <TableRow key={prompt_0.id}>
                        <TableCell>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={prompt_0.imageUrl} alt="Thumbnail" className="h-12 w-12 object-cover rounded" />
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm">
                          {prompt_0.prompt}
                        </TableCell>
                        <TableCell className="text-sm">
                          {prompt_0.width}x{prompt_0.height}
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${prompt_0.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {prompt_0.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(prompt_0.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(prompt_0)} disabled={loading}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(prompt_0.id)} disabled={loading}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>}
          </div>}
      </DialogContent>
    </Dialog>;
}